import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import "./Menu.scss";

export type MenuItem = {
  label: React.ReactNode;
  icon?: React.ReactNode;
  onClick: () => void;
  /** danger = red (Delete) */
  tone?: "default" | "danger";
  disabled?: boolean;
  /** Hidden when false (handy for permission checks inline) */
  visible?: boolean;
};

export type MenuProps = {
  items: MenuItem[];
  /** The trigger's accessible name */
  label?: string;
  /** A custom trigger; defaults to a ⋮ icon button */
  trigger?: (props: { onClick: (e: React.MouseEvent) => void; "aria-expanded": boolean; "aria-haspopup": "menu"; ref: React.Ref<HTMLButtonElement> }) => React.ReactNode;
  /** Which edge of the trigger the menu lines up with */
  align?: "start" | "end";
  className?: string;
};

/** A dropdown of actions (a row's ⋮ menu, a "Create" split…). Renders into document.body so it is never clipped. */
export function Menu({ items, label = "More actions", trigger, align = "end", className }: MenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<React.CSSProperties>({ visibility: "hidden" });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const shown = items.filter((i) => i.visible !== false);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !listRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const w = listRef.current.offsetWidth;
    const h = listRef.current.offsetHeight;
    const left = align === "end" ? Math.max(8, r.right - w) : Math.min(r.left, window.innerWidth - w - 8);
    const below = window.innerHeight - r.bottom - 8 >= h;
    setPos(below ? { left, top: r.bottom + 4 } : { left, bottom: window.innerHeight - r.top + 4 });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const t = e.target as Node;
      if (listRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const onScroll = (e: Event) => {
      if (listRef.current && e.target instanceof Node && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    listRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPos({ visibility: "hidden" });
    setOpen((o) => !o);
  };

  const onListKey = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const buttons = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? []);
    const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = e.key === "ArrowDown" ? (at + 1) % buttons.length : (at - 1 + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  return (
    <>
      {trigger ? (
        trigger({ onClick: toggle, "aria-expanded": open, "aria-haspopup": "menu", ref: triggerRef })
      ) : (
        <button
          ref={triggerRef}
          type="button"
          className={["ui-menu-trigger", open ? "is-open" : "", className].filter(Boolean).join(" ")}
          aria-label={label}
          data-tooltip={label}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={toggle}
        >
          <MoreVertical size={18} />
        </button>
      )}
      {open &&
        createPortal(
          <div ref={listRef} className="ui-menu" role="menu" style={pos} onKeyDown={onListKey} onClick={(e) => e.stopPropagation()}>
            {shown.map((item, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={`ui-menu__item${item.tone === "danger" ? " ui-menu__item--danger" : ""}`}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
              >
                {item.icon ? <span className="ui-menu__icon">{item.icon}</span> : null}
                <span>{item.label}</span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

export default Menu;
